// Color-coded chips indicating which outbound marketplaces an RFQ has been
// posted to. Two render variants share the same channel set + colors from
// shared/sourcing-channels.ts so PartsBase always renders blue, ILS always
// renders purple, etc., regardless of where the chip appears.
//
//   - variant="interactive" — full chip row with click-to-toggle (RFQ detail)
//   - variant="readonly"    — full chip row, no click (RFQ list + customer recent RFQs)

import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  SOURCING_CHANNELS,
  SOURCING_CHANNEL_VALUES,
  type SourcingChannel,
} from '@shared/sourcing-channels';
import { api, type CreateSourcingPostResponse } from '@/lib/api';

type ChipVariant = 'interactive' | 'readonly';

interface Props {
  rfqPublicId?: string;
  postedChannels: readonly SourcingChannel[];
  variant: ChipVariant;
  /** Used by the interactive variant to invalidate the right query after toggle. */
  invalidateKeys?: readonly (readonly unknown[])[];
  /** Readonly only: hide channels that aren't posted (compact list rows). */
  hideUnposted?: boolean;
}

export function SourcingChannelChips({
  rfqPublicId,
  postedChannels,
  variant,
  invalidateKeys,
  hideUnposted,
}: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [pending, setPending] = useState<SourcingChannel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Auto-clear transient toggle feedback after a few seconds so it doesn't
  // linger as the broker moves on.
  useEffect(() => {
    if (!info) return;
    const t = setTimeout(() => setInfo(null), 4_000);
    return () => clearTimeout(t);
  }, [info]);

  const postedSet = new Set(postedChannels);

  const channelsToRender = hideUnposted
    ? SOURCING_CHANNEL_VALUES.filter((c) => postedSet.has(c))
    : SOURCING_CHANNEL_VALUES;

  async function toggle(channel: SourcingChannel) {
    if (!rfqPublicId) return;
    setError(null);
    setInfo(null);
    setPending(channel);
    try {
      const isPosted = postedSet.has(channel);
      if (isPosted) {
        await api<void>(`/api/rfqs/${rfqPublicId}/sourcing-posts/${channel}`, {
          method: 'DELETE',
          tokenGetter: () => getToken(),
        });
      } else {
        const resp = await api<CreateSourcingPostResponse>(
          `/api/rfqs/${rfqPublicId}/sourcing-posts`,
          {
            method: 'POST',
            tokenGetter: () => getToken(),
            body: JSON.stringify({ channel }),
          },
        );
        const cfg = SOURCING_CHANNELS[channel];
        if (resp.stagesAdvanced > 0) {
          const noun = resp.stagesAdvanced === 1 ? 'line' : 'lines';
          setInfo(`Posted to ${cfg.label} · ${resp.stagesAdvanced} ${noun} moved to Sourcing`);
        } else {
          setInfo(`Posted to ${cfg.label}`);
        }
      }
      // Invalidate every query the chip might appear in. Caller passes keys
      // for the specific views they own; we always invalidate the rfq detail
      // and list as a safety net.
      qc.invalidateQueries({ queryKey: ['rfq', rfqPublicId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
      for (const key of invalidateKeys ?? []) {
        qc.invalidateQueries({ queryKey: key });
      }
    } catch (e) {
      setError((e as { message?: string })?.message ?? 'Failed to update');
    } finally {
      setPending(null);
    }
  }

  if (channelsToRender.length === 0 && variant === 'readonly') {
    return null;
  }

  return (
    <div className="inline-flex items-center gap-1 flex-wrap">
      {channelsToRender.map((channel) => {
        const cfg = SOURCING_CHANNELS[channel];
        const isPosted = postedSet.has(channel);
        const isPending = pending === channel;
        const cls = isPosted ? cfg.postedClass : cfg.ghostClass;
        // Buttons are a fingertip target on a phone (36px, 12px text); read-only chips stay compact.
        const base =
          (variant === 'readonly' ? 'px-1.5 text-[10px]' : 'min-h-9 px-2.5 text-xs sm:min-h-0 sm:px-1.5 sm:text-[10px]') +
          ' inline-flex items-center gap-1 py-0.5 font-medium rounded border transition-colors';
        if (variant === 'readonly') {
          return (
            <span key={channel} className={`${base} ${cls}`}>
              {cfg.shortLabel}
            </span>
          );
        }
        return (
          <button
            key={channel}
            type="button"
            disabled={isPending}
            onClick={(e) => {
              // Cards / list rows often wrap chips in a Link; stop the click
              // from navigating when the broker just wanted to toggle.
              e.preventDefault();
              e.stopPropagation();
              toggle(channel);
            }}
            className={`${base} ${cls} ${isPending ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
            title={isPosted ? `Click to remove from ${cfg.label}` : `Click to mark posted to ${cfg.label}`}
            aria-pressed={isPosted}
          >
            {cfg.label}
          </button>
        );
      })}
      {error && variant === 'interactive' && (
        <span className="text-[10px] text-destructive ml-1">{error}</span>
      )}
      {info && variant === 'interactive' && (
        <span className="text-[10px] text-foreground-muted ml-1">{info}</span>
      )}
    </div>
  );
}
